import type {ReactElement} from 'react';
import {useCallback, useMemo} from 'react';
import type {SelectedContextualMenuItems} from '@Components/ai-prompt-box/ai-prompt-box.types';
import type {FooterLeftAction} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-left-actions/ai-prompt-box-footer-left-actions.types';
import {AiPromptBoxContextualMenu} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu';
import {flattenContextualMenuOptions} from '@Components/ai-generation-box/ai-generation-box.library';
import {Text, TextSize} from '@Components/text';
import type {EmailContent, SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import type {GenerateIdeasOriginator} from '@Libraries/ai-email-campaign-content-library';
import {EMAIL_CAMPAIGN_AI_CONTENT_API_PATH, getEmailCmpaignPublishingEntityTypeAndId} from '@Libraries/ai-email-campaign-content-library';
import type {AiWriterSource} from '@Components/ai-writer/ai-writer.types';
import {useAiWriterPersistOnSelect} from '@Components/ai-writer/hooks/use-ai-writer-persist-on-select';
import {reuseOrUseGeneratedContent} from '@Components/ai-writer/ai-writer.library';
import {INITIAL_PROMPT_BOX_CONFIG} from '@Components/email-wizard/components/panel-overview-email-campaign/panel-overview-email-campaign.library';
import type {RewriteEmailSubjectResponse, SubjectAndPreviewText} from '@Components/email-wizard/components/panel-overview-email-campaign/panel-overview-email-campaign.types';
import type {UseEmailContentResponse} from '@Components/ai-writer/adapters/use-email-campaign-writer';

const MAX_CONTEXTUAL_MENU_WIDTH = 369;

const CONTENT_PROMPT_BOX_ID = 'old-email-campaign-content-ai-prompt-box';
const CONTENT_SESSION_STORAGE_KEY = 'old-email-campaign-content-writer';
const USED_KEYS_SESSION_STORAGE_KEY = 'old-email-campaign-content-writer-used-keys';

interface UseEmailCampaignContentWriterParams {
  onContentSelected: (subject: string, previewText: string) => void;
}

interface RecentSubjectAndPreviewResponse {
  subject_line: EmailContent | null;
  preview_text: EmailContent | null;
  content_group_key: string;
}

export function useEmailCampaignContentWriter({onContentSelected}: UseEmailCampaignContentWriterParams): AiWriterSource<SubjectAndPreviewText> {
  const fetchRecents = useCallback(async (): Promise<SubjectAndPreviewText[]> => {
    const response = (await window.PMW.readLocal(`${EMAIL_CAMPAIGN_AI_CONTENT_API_PATH}/getRecents`)) as RecentSubjectAndPreviewResponse[];

    return response.flatMap((item) => {
      if (!item.subject_line || !item.preview_text) {
        return [];
      }

      return [{subject: item.subject_line.content, previewText: item.preview_text.content, id: item.content_group_key}];
    });
  }, []);

  const requestRewrite = useCallback(async (prompt: string, options: SelectedContextualMenuItems, originator: GenerateIdeasOriginator): Promise<RewriteEmailSubjectResponse> => {
    const {entityId, entityType} = getEmailCmpaignPublishingEntityTypeAndId();
    return (await window.PMW.readLocal(`${EMAIL_CAMPAIGN_AI_CONTENT_API_PATH}/rewrite`, {
      prompt,
      configOptions: flattenContextualMenuOptions(options),
      originator,
      entityId,
      entityType,
    })) as RewriteEmailSubjectResponse;
  }, []);

  const onGenerate = useCallback(
    async (prompt: string, options: SelectedContextualMenuItems, originator: GenerateIdeasOriginator): Promise<SubjectAndPreviewText[]> => {
      const response = await requestRewrite(prompt, options, originator);
      const configOptions = flattenContextualMenuOptions(options) as SelectedValueType;
      return response.emailSubjects.map((item) => ({subject: item.subject, previewText: item.previewText, id: '', configOptions}));
    },
    [requestRewrite]
  );

  const requestUseContent = useCallback(async (prompt: string, item: SubjectAndPreviewText): Promise<string> => {
    const {entityId, entityType} = getEmailCmpaignPublishingEntityTypeAndId();
    const response = (await window.PMW.writeLocal(`${EMAIL_CAMPAIGN_AI_CONTENT_API_PATH}/useEmailSubjectLine`, {
      prompt,
      subject: item.subject,
      preview_text: item.previewText,
      configOptions: item.configOptions ?? {},
      entityId,
      entityType,
    })) as UseEmailContentResponse;
    return response.contentGroupKey;
  }, []);

  const requestReuseContent = useCallback(async (contentGroupKey: string): Promise<void> => {
    await window.PMW.writeLocal(`${EMAIL_CAMPAIGN_AI_CONTENT_API_PATH}/reuseAiEmailSubjectLine`, {contentGroupKeyForEmailSubjectLine: contentGroupKey});
  }, []);

  const persist = useCallback(
    async (item: SubjectAndPreviewText, prompt: string): Promise<void> => {
      await reuseOrUseGeneratedContent({
        storageKey: USED_KEYS_SESSION_STORAGE_KEY,
        lookupKey: item.subject,
        existingKey: item.id,
        reuse: requestReuseContent,
        use: () => requestUseContent(prompt, item),
      });
    },
    [requestReuseContent, requestUseContent]
  );

  const onSelected = useCallback(
    (item: SubjectAndPreviewText): void => {
      onContentSelected(item.subject, item.previewText);
    },
    [onContentSelected]
  );

  const onItemSelect = useAiWriterPersistOnSelect<SubjectAndPreviewText>({persist, onSelected});

  const getContextualMenu: FooterLeftAction = useCallback((menuProps): ReactElement => {
    return <AiPromptBoxContextualMenu {...menuProps} maxContextualMenuWidth={MAX_CONTEXTUAL_MENU_WIDTH} />;
  }, []);

  const renderPreviewSubtext = (previewText: string): ReactElement | null => {
    if (!previewText) {
      return null;
    }

    return <Text val={previewText} size={TextSize.SM} className="ct-neutral-primary-disabled" />;
  };

  const renderItem = useCallback((item: SubjectAndPreviewText): ReactElement => {
    return (
      <div className="flexbox flex-column">
        <Text val={item.subject} size={TextSize.MD} className="ct-neutral-primary" />
        {renderPreviewSubtext(item.previewText)}
      </div>
    );
  }, []);

  return useMemo(
    (): AiWriterSource<SubjectAndPreviewText> => ({
      id: CONTENT_PROMPT_BOX_ID,
      sessionStorageKey: CONTENT_SESSION_STORAGE_KEY,
      initialConfig: INITIAL_PROMPT_BOX_CONFIG,
      options: [getContextualMenu],
      fetchRecents,
      onGenerate,
      onItemSelect,
      getItemKey: (item: SubjectAndPreviewText) => item.subject,
      renderItem,
      aiPromptBoxPreviewText: window.i18next.t('pmwjs_subject_line_about'),
      title: window.i18next.t('pmwjs_write_with_ai'),
      showRetryOnError: true,
    }),
    [getContextualMenu, fetchRecents, onGenerate, onItemSelect, renderItem]
  );
}
